import React, { useState, useRef } from 'react';
import { LayoutGrid, SquarePlus } from 'lucide-react';
import Container from '@/components/ui/Container';
import DashboardHeader from '@/components/dashboard/DashboardHeader';
import AppLibraryContainer from '@/components/app-library/AppLibraryContainer';
import AppIdeasContainer, { AppIdeasContainerRef } from '@/components/app-library/AppIdeasContainer';
import AppLibrarySidebar from '@/components/app-library/AppLibrarySidebar';
import SidebarLayout from '@/components/layout/SidebarLayout';
import { AppLibraryProvider } from '@/components/app-library/AppLibraryContext';
import MobilePageWrapper from '@/components/ui/MobilePageWrapper';
import RequestAppModal, { RequestAppFormData } from '@/components/app-library/RequestAppModal';
import AppIdeaGenerationModal from '@/components/app-library/AppIdeaGenerationModal';
import { useToast } from '@/hooks/errors/use-toast';

const AppLibraryPage = () => {
  const [isRequestModalOpen, setIsRequestModalOpen] = useState(false);
  const [isGenerationModalOpen, setIsGenerationModalOpen] = useState(false);
  const [requestData, setRequestData] = useState<RequestAppFormData | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const { toast } = useToast();
  
  // Ref to access AppIdeasContainer methods
  const appIdeasContainerRef = useRef<AppIdeasContainerRef>(null);

  const handleRequestApp = (category?: string) => {
    setSelectedCategory(category || '');
    setIsRequestModalOpen(true);
  };
  
  const handleFormSubmit = (formData: RequestAppFormData) => {
    setRequestData(formData);
    setIsRequestModalOpen(false);
    setIsGenerationModalOpen(true);
  };
  
  const handleGenerationSuccess = async (ideaId: string) => {
    console.log('[AppLibrary] Generated App Idea ID:', ideaId);
    
    // Try to open the lightbox for the new app idea
    if (appIdeasContainerRef.current?.openLightboxForIdea) {
      try {
        // Brief delay to ensure React has processed the state updates
        await new Promise(resolve => setTimeout(resolve, 100));
        appIdeasContainerRef.current.openLightboxForIdea(ideaId);
        
        toast({
          title: 'App Idea Generated!',
          description: 'Your new app idea is ready and opened for viewing.',
          variant: 'success',
        });
      } catch (error) {
        console.error('[AppLibrary] Failed to open lightbox for new app idea:', error);
        // Fallback to showing a toast
        toast({
          title: 'App Idea Generated!',
          description: 'Your new app idea is ready and has been added to the list.',
          variant: 'success',
        });
      }
    } else {
      // Fallback if ref is not available
      toast({
        title: 'App Idea Generated!',
        description: 'Your new app idea is ready and has been added to the list.',
        variant: 'success',
      });
    }
  };

  return (
    <MobilePageWrapper>
      <AppLibraryProvider>
        <Container>
          <DashboardHeader 
            title="App Center"
            description="Browse apps, pin favorites, request custom builds"
            icon={<LayoutGrid size={24} />}
          />
          <div className="space-y-3 md:space-y-6">
            <SidebarLayout
              sidebar={<AppLibrarySidebar onRequestApp={handleRequestApp} />}
            >
              <div className="space-y-3 md:space-y-6">
                <AppLibraryContainer onRequestApp={handleRequestApp} />
                <AppIdeasContainer 
                  ref={appIdeasContainerRef}
                  onRequestApp={handleRequestApp} 
                />
              </div>
            </SidebarLayout>
          </div>
        </Container>

        {/* Request App Modal */}
        <RequestAppModal
          isOpen={isRequestModalOpen}
          onClose={() => setIsRequestModalOpen(false)}
          onSubmit={handleFormSubmit}
          selectedCategory={selectedCategory || undefined}
        />
        
        {/* Generation Modal */}
        <AppIdeaGenerationModal
          isOpen={isGenerationModalOpen}
          onClose={() => setIsGenerationModalOpen(false)}
          requestData={requestData}
          onSuccess={handleGenerationSuccess}
        />
      </AppLibraryProvider>
    </MobilePageWrapper>
  );
};

export default AppLibraryPage;
